import React, { useEffect } from "react";
import ChatHistoryList from "features/dashboard/components/ChatHistoryList";

/**
 * Lucid's history as a column beside the conversation.
 *
 * Not a layer over the page: the thread you are reading stays on screen while
 * you look through the rest. The list inside is the same one the Lucid panel
 * shows in its history popover.
 *
 * Triggered by the history clock icon in the sidebar next to Lucid.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   teamId: number
 *   activeSessionId: string | number | null
 *   onSelectSession: (id) => void
 *   onNewChat: () => void
 *   onDeleted: (id) => void   optional
 *   initialSessions: array
 *   formatSessionTime: accepted for compatibility; rows show their age
 */
export default function ChatHistoryDrawer({
  open,
  onClose,
  teamId,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleted,
  initialSessions,
}) {
  // Escape closes the column from anywhere on the page, but not while a
  // confirm/rename dialog of its own is up: that Escape belongs to the dialog.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <aside
      className="relative flex w-[276px] min-h-0 flex-1 flex-col overflow-hidden rounded-tr-2xl border-l border-t border-[color:var(--border-default)] bg-[color:var(--surface-base)]"
      aria-label="Chat history"
    >
      <ChatHistoryList
        teamId={teamId}
        activeSessionId={activeSessionId}
        onSelect={onSelectSession}
        onNewChat={onNewChat}
        onClose={onClose}
        onDeleted={onDeleted}
        initialSessions={initialSessions}
        showClose
      />
    </aside>
  );
}
